import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loading } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScheduleSettings } from "@/components/settings/ScheduleSettings";
import { LateFeeSettings } from "@/components/settings/LateFeeSettings";
import { NotificationSettings } from "@/components/settings/NotificationSettings";
import { ProfileSettings } from "@/components/settings/ProfileSettings";
import { AdminAccountsCard } from "@/components/admin/AdminAccountsCard";
import { useAuth } from "@/context/AuthContext";
import { Landmark, Smartphone, Building, Save } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { user } = useAuth();
  const [pi, setPi] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings/payment-instructions").then((r) => setPi(r.data)); }, []);

  const set = (k, v) => setPi((p) => ({ ...p, [k]: v }));

  const savePi = async () => {
    setSaving(true);
    try {
      const { owner_id, ...payload } = pi;
      await api.put("/settings/payment-instructions", payload);
      toast.success("Payment details saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  if (!pi) return <Loading />;

  return (
    <div>
      <PageHeader title="Settings" subtitle="Your profile, payment details, inbox forwarding, scheduled notices, and late-fee rules." />

      {user?.is_super && <AdminAccountsCard />}

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="space-y-5">
          <ProfileSettings />
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h2 className="font-display font-bold text-lg text-slate-900 mb-1">Payment instructions</h2>
            <p className="text-sm text-slate-500 mb-5">Tenants see these when paying rent manually.</p>

            <div className="space-y-4">
              <div className="rounded-xl border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-3"><span className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: "#74277420", color: "#742774" }}><Smartphone className="h-4 w-4" /></span><span className="font-semibold text-slate-800">Zelle</span></div>
                <Input value={pi.zelle} onChange={(e) => set("zelle", e.target.value)} placeholder="email or phone" data-testid="settings-zelle" />
              </div>
              <div className="rounded-xl border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-3"><span className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: "#00D63220", color: "#00953a" }}><Smartphone className="h-4 w-4" /></span><span className="font-semibold text-slate-800">CashApp</span></div>
                <Input value={pi.cashapp} onChange={(e) => set("cashapp", e.target.value)} placeholder="$YourCashtag" data-testid="settings-cashapp" />
              </div>
              <div className="rounded-xl border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-3"><span className="h-8 w-8 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center"><Building className="h-4 w-4" /></span><span className="font-semibold text-slate-800">Chase direct deposit</span></div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-3 space-y-1.5"><Label className="text-xs">Account name</Label><Input value={pi.chase_name} onChange={(e) => set("chase_name", e.target.value)} data-testid="settings-chase-name" /></div>
                  <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Account #</Label><Input value={pi.chase_account} onChange={(e) => set("chase_account", e.target.value)} data-testid="settings-chase-account" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Routing #</Label><Input value={pi.chase_routing} onChange={(e) => set("chase_routing", e.target.value)} /></div>
                </div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Memo / notes to tenants</Label><Textarea value={pi.notes} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>
              <Button onClick={savePi} disabled={saving} className="gap-2 w-full" data-testid="settings-save"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save payment details"}</Button>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h2 className="font-display font-bold text-lg text-slate-900 mb-3 flex items-center gap-2"><Landmark className="h-5 w-5 text-primary" /> Online card payments</h2>
            <p className="text-sm text-slate-500 leading-relaxed">
              Tenants can pay their copay (including any late fees) by debit/credit card through secure Stripe checkout from their portal. Paid
              card payments are recorded and confirmed automatically in your ledger.
            </p>
          </div>
        </div>

        <div className="space-y-5">
          <NotificationSettings />
          <ScheduleSettings />
          <LateFeeSettings />
        </div>
      </div>
    </div>
  );
}
